"use client";

import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer";
import * as React from "react";

import { cn, cnState } from "@beweegbaas/ui/lib/utils";

type DrawerDirection = "top" | "bottom" | "left" | "right";

const swipeDirectionByDrawerDirection = {
  top: "up",
  bottom: "down",
  left: "left",
  right: "right",
} satisfies Record<DrawerDirection, DrawerPrimitive.Root.Props["swipeDirection"]>;

const DrawerDirectionContext = React.createContext<DrawerDirection>("bottom");
const DrawerModalContext = React.createContext<boolean | "trap-focus">(true);

function Drawer({
  direction = "bottom",
  shouldScaleBackground: _shouldScaleBackground,
  swipeDirection,
  modal = true,
  ...props
}: DrawerPrimitive.Root.Props & {
  direction?: DrawerDirection;
  shouldScaleBackground?: boolean;
}) {
  return (
    <DrawerDirectionContext.Provider value={direction}>
      <DrawerModalContext.Provider value={modal}>
        <DrawerPrimitive.Root
          data-slot="drawer"
          modal={modal}
          swipeDirection={swipeDirection ?? swipeDirectionByDrawerDirection[direction]}
          {...props}
        />
      </DrawerModalContext.Provider>
    </DrawerDirectionContext.Provider>
  );
}

function DrawerTrigger({ ...props }: DrawerPrimitive.Trigger.Props) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({ ...props }: DrawerPrimitive.Portal.Props) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerClose({ ...props }: DrawerPrimitive.Close.Props) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

function DrawerOverlay({ className, ...props }: DrawerPrimitive.Backdrop.Props) {
  return (
    <DrawerPrimitive.Backdrop
      data-slot="drawer-overlay"
      className={cnState(
        "fixed inset-0 z-50 min-h-dvh bg-black opacity-[calc(var(--backdrop-opacity)*(1-var(--drawer-swipe-progress)))] transition-opacity duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)] [--backdrop-opacity:0.1] data-[ending-style]:opacity-0 data-[ending-style]:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-[starting-style]:opacity-0 data-[swiping]:duration-0 supports-backdrop-filter:backdrop-blur-xs supports-[-webkit-touch-callout:none]:absolute dark:[--backdrop-opacity:0.4]",
        className,
      )}
      {...props}
    />
  );
}

function DrawerContent({
  className,
  innerClassName,
  children,
  ...props
}: DrawerPrimitive.Popup.Props & {
  /** Classes for the inner Content element that directly wraps children */
  innerClassName?: string;
}) {
  const direction = React.useContext(DrawerDirectionContext);
  // Non-modal: backdrop stays visible but click-through, and the full-screen
  // viewport must not swallow page clicks either.
  const nonModal = React.useContext(DrawerModalContext) === false;

  return (
    <DrawerPortal data-slot="drawer-portal">
      {/* Base UI skips the backdrop of a nested drawer by default; forcing it puts a fresh
          overlay over the whole screen, including the drawer underneath. */}
      <DrawerOverlay forceRender className={nonModal ? "pointer-events-none" : undefined} />
      <DrawerPrimitive.Viewport
        data-slot="drawer-viewport"
        className={cn(
          "fixed inset-0 z-50 flex p-[var(--viewport-padding)] [--viewport-padding:0px] supports-[-webkit-touch-callout:none]:[--viewport-padding:0.625rem]",
          nonModal && "pointer-events-none",
          direction === "right" && "items-stretch justify-end",
          direction === "left" && "items-stretch justify-start",
          direction === "bottom" && "items-end justify-stretch",
          direction === "top" && "items-start justify-stretch",
        )}
      >
        <DrawerPrimitive.Popup
          data-slot="drawer-content"
          className={cnState(
            [
              "group/drawer-content bg-popover text-popover-foreground outline-border flex touch-auto flex-col overflow-y-auto overscroll-contain text-sm outline-1 transition-transform duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)] data-[ending-style]:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-[swiping]:duration-0 data-[swiping]:select-none",
              nonModal && "pointer-events-auto",
              direction === "right" &&
                "-mr-[3rem] h-full w-[calc(23rem+3rem)] max-w-[calc(100vw-3rem+3rem)] [transform:translateX(var(--drawer-swipe-movement-x))] pr-[calc(1.5rem+3rem)]! [--bleed:3rem] data-[ending-style]:[transform:translateX(calc(100%-var(--bleed)+var(--viewport-padding)+2px))] data-[starting-style]:[transform:translateX(calc(100%-var(--bleed)+var(--viewport-padding)+2px))] supports-[-webkit-touch-callout:none]:mr-0 supports-[-webkit-touch-callout:none]:w-[23rem] supports-[-webkit-touch-callout:none]:max-w-[calc(100vw-20px)] supports-[-webkit-touch-callout:none]:rounded-[10px] supports-[-webkit-touch-callout:none]:pr-6 supports-[-webkit-touch-callout:none]:[--bleed:0px]",
              direction === "left" &&
                "-ml-[3rem] h-full w-[calc(23rem+3rem)] max-w-[calc(100vw-3rem+3rem)] [transform:translateX(var(--drawer-swipe-movement-x))] pl-[calc(1.5rem+3rem)]! [--bleed:3rem] data-[ending-style]:[transform:translateX(calc(-100%+var(--bleed)-var(--viewport-padding)-2px))] data-[starting-style]:[transform:translateX(calc(-100%+var(--bleed)-var(--viewport-padding)-2px))] supports-[-webkit-touch-callout:none]:ml-0 supports-[-webkit-touch-callout:none]:w-[23rem] supports-[-webkit-touch-callout:none]:max-w-[calc(100vw-20px)] supports-[-webkit-touch-callout:none]:rounded-[10px] supports-[-webkit-touch-callout:none]:pl-6 supports-[-webkit-touch-callout:none]:[--bleed:0px]",
              direction === "bottom" &&
                "max-h-[80vh] w-full [transform:translateY(calc(var(--drawer-snap-point-offset)+var(--drawer-swipe-movement-y)))] rounded-t-xl border-t data-[ending-style]:[transform:translateY(100%)] data-[starting-style]:[transform:translateY(100%)]",
              direction === "top" &&
                "max-h-[80vh] w-full [transform:translateY(var(--drawer-swipe-movement-y))] rounded-b-xl border-b data-[ending-style]:[transform:translateY(-100%)] data-[starting-style]:[transform:translateY(-100%)]",
            ],
            className,
          )}
          {...props}
        >
          <div className="bg-muted mx-auto mt-4 hidden h-1 w-[100px] shrink-0 rounded-full group-data-[swipe-direction=down]/drawer-content:block" />
          <DrawerPrimitive.Content
            data-slot="drawer-inner-content"
            // With a DrawerBody the body scrolls instead of the popup, keeping the footer in view.
            className={cn(
              "has-[>[data-slot=drawer-body]]:flex has-[>[data-slot=drawer-body]]:h-full has-[>[data-slot=drawer-body]]:flex-col",
              innerClassName,
            )}
          >
            {children}
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        "flex flex-col gap-0.5 p-4 group-data-[swipe-direction=down]/drawer-content:text-center group-data-[swipe-direction=up]/drawer-content:text-center md:gap-0.5 md:text-left",
        className,
      )}
      {...props}
    />
  );
}

function DrawerBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-body"
      className={cn("scroll-fade-y min-h-0 flex-1", className)}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn("mt-auto flex shrink-0 flex-col gap-2 border-t p-4", className)}
      {...props}
    />
  );
}

function DrawerTitle({ className, ...props }: DrawerPrimitive.Title.Props) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cnState("cn-font-heading text-foreground text-base font-medium", className)}
      {...props}
    />
  );
}

function DrawerDescription({ className, ...props }: DrawerPrimitive.Description.Props) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cnState("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

// DrawerPrimitive exported for advanced use cases
export {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerPrimitive,
  DrawerTitle,
  DrawerTrigger,
};
